import type { Candle, Signal, Trade, TradeMode } from "@ct/shared-types";
import { apiFetch } from "./auth.js";

export async function fetchCandles(params: {
  exchange: string;
  symbol: string;
  timeframe: string;
  limit?: number;
}): Promise<Candle[]> {
  const search = new URLSearchParams({
    exchange: params.exchange,
    symbol: params.symbol,
    timeframe: params.timeframe,
    limit: String(params.limit ?? 500),
  });
  const res = await apiFetch(`/v1/market/candles?${search}`);
  if (!res.ok) throw new Error(`fetchCandles ${res.status}`);
  const json = (await res.json()) as { candles: Candle[] };
  return json.candles;
}

export interface AdapterHealth {
  connected: boolean;
  ts?: number;
  reason?: string;
}

export async function fetchAdapterHealth(): Promise<Record<string, AdapterHealth>> {
  const res = await apiFetch(`/v1/health/adapters`);
  if (!res.ok) throw new Error(`fetchAdapterHealth ${res.status}`);
  const json = (await res.json()) as { adapters: Record<string, AdapterHealth> };
  return json.adapters;
}

export async function fetchRecentSignals(limit = 100): Promise<Signal[]> {
  const res = await apiFetch(`/v1/signals/recent?limit=${limit}`);
  if (!res.ok) throw new Error(`fetchRecentSignals ${res.status}`);
  const json = (await res.json()) as { signals: Signal[] };
  return json.signals;
}

export interface ScannerItem {
  id: string;
  strategyId: string;
  exchange: string;
  symbol: string;
  timeframe: string;
  direction: "LONG";
  entryPrice: number;
  stopLoss: number;
  takeProfit1?: number;
  compositeScore: number;
  confidence: number;
  marketRegime: string | null;
  issuedAt: number;
  expiresAt: number;
}

export interface ScannerPayload {
  items: ScannerItem[];
  ts: number;
}

export async function fetchScannerTop(): Promise<ScannerPayload> {
  const res = await apiFetch(`/v1/scanner/top`);
  if (!res.ok) throw new Error(`fetchScannerTop ${res.status}`);
  return (await res.json()) as ScannerPayload;
}

export interface LeaderboardRow {
  strategyId: string;
  window: string;
  tradesTotal: number;
  tradesWon: number;
  tradesLost: number;
  winRate: number;
  profitFactor: number;
  sharpe: number;
  sortino: number;
  maxDrawdownPct: number;
  expectancy: number;
  compositeScore: number;
  rank: number;
  isPrimary: boolean;
  computedAt: number;
}

export async function fetchLeaderboard(
  window: "24h" | "7d" | "30d" | "all" = "30d",
): Promise<LeaderboardRow[]> {
  const res = await apiFetch(`/v1/leaderboard?window=${window}`);
  if (!res.ok) throw new Error(`fetchLeaderboard ${res.status}`);
  const json = (await res.json()) as { strategies: LeaderboardRow[] };
  return json.strategies;
}

export interface RegimePayload {
  exchange: string;
  symbol: string;
  regime: string;
  components: Record<string, number>;
  lastClose: number | null;
  ts: number;
}

export async function fetchRegime(
  exchange: string,
  symbol: string,
): Promise<RegimePayload | null> {
  const search = new URLSearchParams({ exchange, symbol });
  const res = await apiFetch(`/v1/regime?${search}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`fetchRegime ${res.status}`);
  return (await res.json()) as RegimePayload;
}

export async function fetchOpenTrades(mode: TradeMode): Promise<Trade[]> {
  const res = await apiFetch(`/v1/trades/open?mode=${mode}`);
  if (!res.ok) throw new Error(`fetchOpenTrades ${res.status}`);
  const json = (await res.json()) as { trades: Trade[] };
  return json.trades;
}

export async function fetchRecentTrades(
  mode: TradeMode,
  limit = 100,
): Promise<Trade[]> {
  const res = await apiFetch(`/v1/trades/recent?mode=${mode}&limit=${limit}`);
  if (!res.ok) throw new Error(`fetchRecentTrades ${res.status}`);
  const json = (await res.json()) as { trades: Trade[] };
  return json.trades;
}

export interface PaperAccountRow {
  id: string;
  strategyId: string;
  startingBalance: number;
  currentBalance: number;
  equity: number;
  realizedPnl: number;
}

export async function fetchPaperAccounts(): Promise<PaperAccountRow[]> {
  const res = await apiFetch(`/v1/paper/accounts`);
  if (!res.ok) throw new Error(`fetchPaperAccounts ${res.status}`);
  const json = (await res.json()) as { accounts: PaperAccountRow[] };
  return json.accounts;
}

export interface PortfolioHolding {
  exchange: string;
  asset: string;
  quoteCurrency: string;
  quantity: number;
  avgCostBasis: number;
  lastPrice: number;
  value: number;
  unrealizedPnl: number;
  unrealizedPnlPct: number;
  lastUpdated: number;
}

export interface PortfolioSummary {
  holdings: PortfolioHolding[];
  totals: {
    value: number;
    cost: number;
    unrealizedPnl: number;
    unrealizedPnlPct: number;
  };
}

export async function fetchPortfolio(): Promise<PortfolioSummary> {
  const res = await apiFetch(`/v1/portfolio`);
  if (!res.ok) throw new Error(`fetchPortfolio ${res.status}`);
  return (await res.json()) as PortfolioSummary;
}

export interface DcaPlan {
  id: string;
  exchange: string;
  symbol: string;
  quoteAmount: number;
  quoteCurrency: string;
  frequency: "DAILY" | "WEEKLY" | "BIWEEKLY" | "MONTHLY";
  nextRunAt: number;
  status: "ACTIVE" | "PAUSED" | "COMPLETED" | "CANCELLED";
  totalInvested: number;
  totalQuantity: number;
  createdAt: number;
}

export async function fetchDcaPlans(): Promise<DcaPlan[]> {
  const res = await apiFetch("/v1/dca");
  if (!res.ok) throw new Error(`fetchDcaPlans ${res.status}`);
  return ((await res.json()) as { plans: DcaPlan[] }).plans;
}

export async function createDcaPlan(input: Omit<DcaPlan, "id" | "status" | "totalInvested" | "totalQuantity" | "createdAt">): Promise<string> {
  const res = await apiFetch("/v1/dca", {
    method: "POST",
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`createDcaPlan ${res.status}`);
  const json = (await res.json()) as { id: string };
  return json.id;
}

export async function deleteDcaPlan(id: string): Promise<void> {
  const res = await apiFetch(`/v1/dca/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`deleteDcaPlan ${res.status}`);
}

export interface PriceAlert {
  id: string;
  exchange: string;
  symbol: string;
  condition: "CROSS_ABOVE" | "CROSS_BELOW" | "CHANGE_PCT_24H";
  threshold: number;
  notifyEmail: boolean;
  notifyPush: boolean;
  status: "ACTIVE" | "TRIGGERED" | "EXPIRED" | "CANCELLED";
  triggeredAt: number | null;
  triggeredPrice: number | null;
  createdAt: number;
}

export async function fetchAlerts(): Promise<PriceAlert[]> {
  const res = await apiFetch("/v1/alerts");
  if (!res.ok) throw new Error(`fetchAlerts ${res.status}`);
  return ((await res.json()) as { alerts: PriceAlert[] }).alerts;
}

export async function createAlert(input: Omit<PriceAlert, "id" | "status" | "triggeredAt" | "triggeredPrice" | "createdAt">): Promise<string> {
  const res = await apiFetch("/v1/alerts", {
    method: "POST",
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`createAlert ${res.status}`);
  return ((await res.json()) as { id: string }).id;
}

export async function deleteAlert(id: string): Promise<void> {
  const res = await apiFetch(`/v1/alerts/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`deleteAlert ${res.status}`);
}

export interface ExchangeKeyRow {
  id: string;
  exchange: string;
  keyLabel: string;
  scopeRead: boolean;
  scopeTrade: boolean;
  lastTestedAt: number | null;
  lastTestStatus: string | null;
  createdAt: number;
}

export async function fetchExchangeKeys(): Promise<ExchangeKeyRow[]> {
  const res = await apiFetch("/v1/exchange-keys");
  if (!res.ok) throw new Error(`fetchExchangeKeys ${res.status}`);
  return ((await res.json()) as { keys: ExchangeKeyRow[] }).keys;
}

export async function createExchangeKey(input: {
  exchange: string;
  apiKey: string;
  apiSecret: string;
  scopeRead?: boolean;
  scopeTrade?: boolean;
}): Promise<string> {
  const res = await apiFetch("/v1/exchange-keys", {
    method: "POST",
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`createExchangeKey ${res.status}`);
  return ((await res.json()) as { id: string }).id;
}

export async function deleteExchangeKey(id: string): Promise<void> {
  const res = await apiFetch(`/v1/exchange-keys/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`deleteExchangeKey ${res.status}`);
}
